'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * Reports a page view on every client-side navigation.
 *
 * The gtag snippet sends one view when it loads and then nothing, because a
 * Next.js route change replaces the page without a document load. Left alone
 * that reports the entry page and never the ones a visitor actually read, so
 * every internal journey — home to a service to the booking form — collapses
 * into a single hit on the home page, and the funnel question this was
 * installed to answer stays unanswerable.
 *
 * Only the path is watched. Query strings on this site carry no page identity,
 * and reading them here would force the whole layout behind a Suspense
 * boundary for no gain.
 */
export default function AnalyticsPageViews({ tagId }: { tagId: string }) {
  const pathname = usePathname();
  /* The snippet already reported the first page; counting it again here would
     double every landing. */
  const reported = useRef(true);

  useEffect(() => {
    if (reported.current) {
      reported.current = false;
      return;
    }
    window.gtag?.('event', 'page_view', {
      send_to: tagId,
      page_location: window.location.href,
      page_title: document.title,
      page_path: pathname,
    });
  }, [pathname, tagId]);

  return null;
}
